import { StandardFontEmbedder, StandardFonts } from 'pdf-lib';

import {
  ENTRIES,
  ENTRY,
  isFormOnly,
  rowsOf,
  type AnswerType,
  type Condition,
  type Entry,
  type EntryOption,
} from '@/james-playground/aa-family-claim/claim/registry';
import {
  isEmpty,
  type Facts,
  type Row,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  evaluate,
  statusOf,
  type Tri,
} from '@/james-playground/aa-family-claim/claim/engine';

/**
 * The AA1 11/25 PDF, box by box, from the claim's facts.
 *
 * Every mapping comes from the registry: the widget an answer goes to, the
 * on-state a tick takes, and when a question applies. The rules added here:
 *
 * - Only confirmed answers reach the form, and only for entries that apply.
 *   Proposed, conflicting and skipped answers leave their boxes blank.
 * - Signatures, signature dates and the page 31 checklist stay blank; the
 *   signer completes them by hand. Box 30-1 (the worked example) is never in
 *   the registry, so it is never written.
 * - Text that will not fit its box is cut at a word, ends "(continued on page
 *   29)", and the full answer is carried into Q63 under its question number.
 *   Q63 holds the family's own extra information and those carried answers,
 *   nothing else. If Q63 itself overflows, the rest goes on separate sheets.
 *
 * Text is laid out the way pdf-lib lays it out (pdf-lib 1.17, layout.ts),
 * but each line is measured as viewers draw it, and the filler draws exactly
 * these lines. So "fits" here means fits on paper.
 */

export type WidgetValue = string | boolean;

export type AaWidgetValues = {
  /** Widget name to value: the text for a text box, the on-state for a tick box. */
  values: Record<string, WidgetValue>;
  /** Answers carried into Q63, in form order, each under its question number. */
  continuation: string[];
  /** Q63 text that did not fit Q63 itself, for separate sheets. Usually empty. */
  extraSheets: string[];
};

// ---- Measuring ------------------------------------------------------------

/** The template's text size: every text field's DA is "/ArialMT 10 Tf". */
export const FONT_SIZE = 10;

/** pdf-lib's StandardFonts and the embedder's own font names are the same strings. */
type FontName = Parameters<typeof StandardFontEmbedder.for>[0];

/** Arial and Helvetica share their widths, so Helvetica measures the template's boxes. */
const HELVETICA = StandardFontEmbedder.for(
  StandardFonts.Helvetica as string as FontName,
);

/** pdf-lib steps multiline text by 1.2 times the font height: 11.1pt at 10pt. */
const LINE_HEIGHT = HELVETICA.heightOfFontAtSize(FONT_SIZE) * 1.2;

/** The sum of the glyph advances: viewers never kern a Tj string. */
const drawnWidth = (text: string) =>
  ([...text].reduce(
    (units, c) =>
      units +
      (HELVETICA.font.getWidthOfGlyph(
        HELVETICA.encoding.encodeUnicodeCodePoint(c.codePointAt(0) as number)
          .name,
      ) ?? 0),
    0,
  ) *
    FONT_SIZE) /
  1000;

/**
 * A line's width for fitting: the wider of what viewers draw and what pdf-lib
 * measures. pdf-lib subtracts kerning, which viewers never apply, so a line it
 * packs to the edge is drawn up to 1pt past the box; Helvetica also has
 * positive kerning pairs, so neither measure alone is safe. A line narrower
 * than the box on both is drawn whole and never broken again by pdf-lib.
 */
export const widthOf = (text: string) =>
  Math.max(drawnWidth(text), HELVETICA.widthOfTextAtSize(text, FONT_SIZE));

/** Q63 is on page 29 of the AA1. */
export const CONTINUED = '(continued on page 29)';
/** Shorter pointers, for boxes too narrow to hold the full one. */
const POINTERS = [CONTINUED, 'See page 29', 'See p29', 'p29'];
const Q63_WIDGET = '354';
const Q63_CONTINUED = '(continued on a separate sheet)';

/**
 * A text box as pdf-lib lays it out: the usable width and height (the widget
 * rectangle less a 1pt border and 1pt padding on each side, no padding on a
 * comb field), and "s" single line, "m" multiline, or a comb field's cell
 * count. The filler checks this table against the template on every fill.
 */
export type Box = [width: number, height: number, kind: 's' | 'm' | number];

// prettier-ignore
export const BOXES: Record<string, Box> = {
  '01-01i': [213.43, 18.96, 's'],
  '01-06i': [213.43, 18.96, 's'],
  '01-01i 2': [213.43, 18.96, 's'],
  '01-06ii': [213.43, 18.96, 's'],
  '01-01ii': [213, 40.53, 'm'],
  '7': [213.43, 18.96, 's'],
  '01-01iii': [213.43, 18.96, 's'],
  '01-02NINO': [214, 20.96, 9],
  '01-03Date': [122.45, 18.96, 's'],
  '01-05line1': [213.43, 18.96, 's'],
  '01-05line2': [213.43, 18.96, 's'],
  '01-05line3': [213.43, 18.96, 's'],
  '01-05line4': [161.49, 18.96, 's'],
  '9_1': [122.46, 18.96, 's'],
  '9_2': [122.46, 18.96, 's'],
  '9_3': [213.43, 18.96, 's'],
  '9_4': [213.43, 131.36, 'm'],
  '13_2': [122.45, 18.96, 's'],
  '15_2': [213.43, 18.96, 's'],
  '15_4': [214, 20.96, 9],
  '15_5': [122.45, 18.96, 's'],
  '15_6': [213.43, 18.96, 's'],
  '15_7': [213.43, 18.96, 's'],
  '15_8': [213.43, 18.96, 's'],
  '15_9': [161.49, 18.96, 's'],
  '15_3': [213.43, 18.96, 's'],
  '15_10': [213.43, 18.96, 's'],
  '16_1': [94.21, 44.61, 'm'],
  '16_2': [94.21, 44.61, 'm'],
  '16_3': [180.02, 44.61, 'm'],
  '16_4': [94.21, 44.61, 'm'],
  '16_5': [94.21, 44.61, 'm'],
  '16_6': [94.21, 44.61, 'm'],
  '16_7': [180.02, 44.61, 'm'],
  '16_8': [94.21, 44.61, 'm'],
  '16_9': [94.21, 44.61, 'm'],
  '16_10': [94.21, 44.61, 'm'],
  '16_11': [180.02, 44.61, 'm'],
  '16_12': [94.21, 44.61, 'm'],
  '16_13': [94.21, 44.61, 'm'],
  '16_14': [94.21, 44.61, 'm'],
  '16_15': [180.02, 44.61, 'm'],
  '16_16': [94.21, 44.61, 'm'],
  '16_17': [94.21, 44.61, 'm'],
  '16_18': [94.21, 44.61, 'm'],
  '16_19': [180.02, 44.61, 'm'],
  '16_20': [94.21, 44.61, 'm'],
  '16_21': [94.21, 44.61, 'm'],
  '16_22': [94.21, 44.61, 'm'],
  '16_23': [180.02, 44.61, 'm'],
  '16_24': [94.21, 44.61, 'm'],
  '17_10': [213.43, 18.96, 's'],
  '17_11': [122.45, 18.96, 's'],
  '17_1': [213.43, 18.96, 's'],
  '17_2': [213.43, 18.96, 's'],
  '17_3': [213.43, 18.96, 's'],
  '17_4': [213.43, 18.96, 's'],
  '18_1': [213.43, 18.96, 's'],
  '17_5': [213.43, 18.96, 's'],
  '17_6': [161.49, 18.96, 's'],
  '18_2': [213.43, 18.96, 's'],
  '18_3': [213.43, 18.96, 's'],
  '18_4': [213.43, 18.96, 's'],
  '17_7': [213.43, 18.96, 's'],
  '18_5': [161.49, 18.96, 's'],
  '18_6': [213.43, 18.96, 's'],
  '17_8': [213.43, 18.96, 's'],
  '18_7': [214.43, 85.65, 'm'],
  '17_9': [214.43, 85.65, 'm'],
  '18_8': [213.43, 18.96, 's'],
  '18_9': [213.43, 18.96, 's'],
  '19': [213.43, 18.96, 's'],
  '19_1': [213.43, 18.96, 's'],
  '19-2': [213.43, 18.96, 's'],
  '19-3': [213.43, 18.96, 's'],
  '19-4': [161.49, 18.96, 's'],
  '19-5': [213.43, 18.96, 's'],
  '19-6': [122.45, 18.96, 's'],
  '22-1': [155.72, 48.89, 'm'],
  '22-2': [155.72, 48.89, 'm'],
  '22-3': [135.97, 48.89, 'm'],
  '22-4': [155.72, 48.89, 'm'],
  '22-5': [155.72, 48.89, 'm'],
  '22-6': [135.97, 48.89, 'm'],
  '23-1': [180.73, 49.5, 'm'],
  '23-2': [271.59, 49.5, 'm'],
  '23-3': [180.73, 49.5, 'm'],
  '23-4': [271.59, 49.5, 'm'],
  '24': [213.43, 18.96, 's'],
  '26-4': [191.97, 18.96, 's'],
  '25-3': [191.97, 18.96, 's'],
  '27-1': [87.42, 74.37, 'm'],
  '27-3': [168.26, 74.37, 'm'],
  '27-4': [181.59, 74.37, 'm'],
  '27-5': [87.42, 74.37, 'm'],
  '27-7': [168.26, 74.37, 'm'],
  '27-8': [181.59, 74.37, 'm'],
  '27-9': [87.42, 74.37, 'm'],
  '27-11': [168.26, 74.37, 'm'],
  '27-12': [181.59, 74.37, 'm'],
  '27-13': [87.42, 74.37, 'm'],
  '27-15': [168.26, 74.37, 'm'],
  '27-16': [181.59, 74.37, 'm'],
  '27-17': [87.42, 74.37, 'm'],
  '27-19': [168.26, 74.37, 'm'],
  '27-20': [181.59, 74.37, 'm'],
  '27-21': [87.42, 74.37, 'm'],
  '27-23': [168.26, 74.37, 'm'],
  '27-24': [181.59, 74.37, 'm'],
  '28': [122.45, 18.96, 's'],
  '29-8': [192.59, 129.91, 'm'],
  '30-7': [130.5, 18.96, 's'],
  '30-9': [189.76, 130.57, 'm'],
  '30-2': [130.5, 18.96, 's'],
  '30-3': [130.5, 18.96, 's'],
  '30-4': [130.5, 18.96, 's'],
  '30-5': [130.5, 18.96, 's'],
  '31-1': [130.5, 18.96, 's'],
  '30-6': [130.5, 18.96, 's'],
  '31-2': [130.5, 18.96, 's'],
  '31-10': [130.5, 18.96, 's'],
  '31-3': [130.5, 18.96, 's'],
  '31-4': [130.5, 18.96, 's'],
  '31-5': [130.5, 18.96, 's'],
  '31-12': [189.76, 130.57, 'm'],
  '31-6': [130.5, 18.96, 's'],
  '31-7': [130.5, 18.96, 's'],
  '31-8': [130.5, 18.96, 's'],
  '32-1': [130.5, 18.96, 's'],
  '31-9': [130.5, 18.96, 's'],
  '32-2': [130.5, 18.96, 's'],
  '32-3': [130.5, 18.96, 's'],
  '32-4': [130.5, 18.96, 's'],
  '32-5': [130.5, 18.96, 's'],
  '32-10': [189.76, 108.42, 'm'],
  '32-6': [130.5, 18.96, 's'],
  '32-7': [130.5, 18.96, 's'],
  '32-8': [130.5, 18.96, 's'],
  '33-11': [188.76, 129.55, 'm'],
  '34-4': [213, 108.2, 'm'],
  '34-5': [130.5, 18.96, 's'],
  '34-6': [130.5, 18.96, 's'],
  '34-7': [130.5, 18.96, 's'],
  '34-8': [130.5, 18.96, 's'],
  '34-1': [213, 108.2, 'm'],
  '34-9': [130.5, 18.96, 's'],
  '34-10': [130.5, 18.96, 's'],
  '35-7': [188.76, 129.55, 'm'],
  '35-1': [130.5, 18.96, 's'],
  '35-2': [130.5, 18.96, 's'],
  '35-3': [130.5, 18.96, 's'],
  '35-4': [130.5, 18.96, 's'],
  '36-1': [130.5, 18.96, 's'],
  '36-2': [130.5, 18.96, 's'],
  '35-5': [130.5, 18.96, 's'],
  '36-3': [130.5, 18.96, 's'],
  '36-4': [130.5, 18.96, 's'],
  '36-5': [130.5, 18.96, 's'],
  '36-6': [130.5, 18.96, 's'],
  '36-8': [188.76, 199.1, 'm'],
  '38': [41.2, 18.96, 's'],
  '37-14': [194.43, 108.19, 'm'],
  '39-1': [123.33, 57.36, 'm'],
  '39-2': [182.09, 57.36, 'm'],
  '39-3': [142.4, 57.36, 'm'],
  '39-4': [123.33, 57.36, 'm'],
  '39-5': [182.09, 57.36, 'm'],
  '39-6': [142.4, 57.36, 'm'],
  '39-7': [123.33, 57.36, 'm'],
  '39-8': [182.09, 57.36, 'm'],
  '39-9': [142.4, 57.36, 'm'],
  '39-10': [123.33, 57.36, 'm'],
  '39-11': [182.09, 57.36, 'm'],
  '39-12': [141.9, 57.36, 'm'],
  '39-13': [123.33, 57.36, 'm'],
  '39-14': [182.09, 57.36, 'm'],
  '39-15': [141.9, 57.36, 'm'],
  '39-16': [123.33, 57.36, 'm'],
  '39-17': [182.09, 57.36, 'm'],
  '39-18': [141.9, 57.36, 'm'],
  '40-10': [213.43, 18.96, 's'],
  '41': [55.53, 18.96, 's'],
  '40-12': [188.76, 108.25, 'm'],
  '42-4': [55.53, 18.96, 's'],
  '42-6': [55.53, 18.96, 's'],
  '42-2': [55.53, 18.96, 's'],
  '42-8': [55.53, 18.96, 's'],
  '42-16': [55.53, 18.96, 's'],
  '42-10': [55.53, 18.96, 's'],
  '42-12': [55.53, 18.96, 's'],
  '42-18': [188.76, 289.25, 'm'],
  '43': [55.53, 18.96, 's'],
  '42-14': [55.53, 18.96, 's'],
  '310': [55.53, 18.96, 's'],
  '311': [213.43, 18.96, 's'],
  '313': [188.76, 536.29, 'm'],
  '314': [55.53, 18.96, 's'],
  '315': [481.43, 626.77, 'm'],
  'Date 326': [122.45, 18.96, 's'],
  'Date 317': [122.45, 18.96, 's'],
  'Date 3274': [122.45, 18.96, 's'],
  '356': [213.43, 18.96, 's'],
  '355': [213.43, 18.96, 's'],
  '328': [213.43, 18.96, 's'],
  '353': [213.43, 18.96, 's'],
  '329': [213.43, 18.96, 's'],
  '352': [161.49, 18.96, 's'],
  '330': [213.43, 18.96, 's'],
  '331': [161.49, 18.96, 's'],
  '322': [213, 85.65, 'm'],
  '332': [211.73, 86.26, 'm'],
  '324': [213, 40.53, 'm'],
  '335': [213.43, 18.96, 's'],
  '336': [213.43, 18.96, 's'],
  '337': [39.59, 20.96, 2],
  '338': [39.59, 20.96, 2],
  '339': [39.59, 20.96, 2],
  '340': [213.43, 18.96, 's'],
  '341': [377.98, 18.96, 's'],
  '347': [213.43, 18.96, 's'],
  '343': [213.43, 18.96, 's'],
  '348': [213.43, 18.96, 's'],
  '349': [213.43, 18.96, 's'],
  '350': [161.49, 18.96, 's'],
  '351': [213.43, 18.96, 's'],
  '344': [213, 108.2, 'm'],
  '345': [213, 40.53, 'm'],
  '346': [213.43, 18.96, 's'],
  '354': [481.43, 626.77, 'm'],
  '357': [213.43, 18.96, 's'],
  '364': [213, 221.01, 'm'],
};

/** Stand-ins for letters and marks Helvetica's WinAnsi encoding cannot draw. */
const LOOKALIKE: Record<string, string> = {
  ł: 'l', Ł: 'L', đ: 'd', Đ: 'D', ı: 'i', ħ: 'h', Ħ: 'H',
  '‐': '-', '‑': '-', '‒': '-', '−': '-',
  '′': "'", '″': '"',
};
const encodable = (c: string) =>
  HELVETICA.encoding.canEncodeUnicodeCodePoint(c.codePointAt(0) as number);

/**
 * The text as the form can print it: one kind of line break, no tabs, and any
 * character outside WinAnsi replaced by its unaccented letter or a lookalike.
 */
export const printable = (text: string): string =>
  [
    ...text
      .replace(/\r\n?|[\f\v\u0085\u2028\u2029]/g, '\n')
      .replace(/\t/g, ' ')
      .trim(),
  ]
    .map((c) => {
      if (c === '\n' || encodable(c)) return c;
      if (/[\u0000-\u001f]/.test(c)) return ' ';
      const bare = c.normalize('NFD').replace(/\p{M}/gu, '');
      if (bare && [...bare].every(encodable)) return bare;
      return LOOKALIKE[c] ?? '?';
    })
    .join('');

/**
 * The lines a multiline box shows, broken as pdf-lib breaks them
 * (layoutMultilineText) but measured with `widthOf`. Each line is the longest
 * start of what is left, cut before a whitespace character (or all of it),
 * that is narrower than the box; the rest is trimmed and laid out the same
 * way. A word wider than the box is drawn whole, past the edge, so it is
 * reported clipped. The filler draws these lines as they are.
 *
 * pdf-lib tries the cuts longest first. Text only widens as it lengthens, so
 * trying them shortest first and stopping at the first one too wide finds the
 * same line without re-measuring the whole paragraph for every line.
 */
export const layoutLines = (
  text: string,
  maxWidth: number,
): { lines: string[]; clipped: boolean } => {
  const lines: string[] = [];
  let clipped = false;
  for (const paragraph of text.split('\n')) {
    let rest: string | undefined = paragraph;
    while (rest !== undefined) {
      // pdf-lib never cuts before the first character.
      const cuts = [...rest.matchAll(/\s/g)]
        .map((m) => m.index as number)
        .filter((i) => i > 0);
      cuts.push(rest.length);
      let cut = 0;
      for (const c of cuts) {
        if (widthOf(rest.substring(0, c)) >= maxWidth) break;
        cut = c;
      }
      if (cut === 0) {
        if (widthOf(rest) >= maxWidth) clipped = true;
        lines.push(rest);
        rest = undefined;
      } else {
        lines.push(rest.substring(0, cut));
        const remainder: string | undefined = rest.substring(cut) || undefined;
        rest = remainder?.trim();
      }
    }
  }
  return { lines, clipped };
};

const singleLine = (text: string) => text.replace(/\n+/g, ' ');

/** Whether the text shows in full in the box, as pdf-lib draws it. */
export const fits = (text: string, [width, height, kind]: Box): boolean => {
  // A comb box centres one character per cell; pdf-lib refuses more than it has.
  if (typeof kind === 'number') return [...text].length <= kind;
  if (kind === 's') return widthOf(singleLine(text)) <= width;
  const { lines, clipped } = layoutLines(text, width);
  return !clipped && lines.length * LINE_HEIGHT <= height;
};

/** The longest start of the text, cut at a space, that fits with the pointer after it. */
const startThatFits = (text: string, box: Box, pointer: string): string => {
  const cuts = [...text.matchAll(/\s/g)].map((m) => m.index as number);
  const at = (k: number) => `${text.slice(0, cuts[k]).trimEnd()} ${pointer}`;
  let lo = 0;
  let hi = cuts.length - 1;
  let best = pointer;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (fits(at(mid), box)) {
      best = at(mid);
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return best;
};

/**
 * What goes in a box too small for the answer: as much as fits, then where the
 * rest is. Blank only when not even "p29" fits; the answer is still in Q63.
 */
const shortened = (text: string, box: Box): string => {
  const pointer = POINTERS.find((p) => fits(p, box));
  return pointer ? startThatFits(text, box, pointer) : '';
};

// ---- Headings for carried answers ----------------------------------------

/** Short names for the form's questions, for answers carried into Q63. */
const TITLE: Record<number, string> = {
  1: 'Special rules',
  2: 'Your name',
  3: 'National Insurance number',
  4: 'Date of birth',
  5: 'Sex',
  6: 'Address',
  7: 'Phone numbers',
  8: 'Nationality',
  9: 'Living in Great Britain',
  10: 'Time abroad',
  11: 'Benefits from an EEA state or Switzerland',
  12: 'Work or insurance in an EEA state or Switzerland',
  13: 'Moving from Scotland',
  14: 'Pension Age Disability Payment',
  15: 'Signing for someone else',
  16: 'Illnesses and disabilities',
  17: 'Other professionals you have seen',
  18: 'People who help you',
  19: 'Your GP',
  20: 'Consent',
  21: 'Reports',
  22: 'Waiting list for surgery',
  23: 'Tests',
  24: 'Accommodation',
  25: 'Where the toilet is',
  26: 'Where you sleep',
  27: 'Aids and adaptations',
  28: 'When your care needs started',
  29: 'Getting in and out of bed',
  30: 'Toilet needs',
  31: 'Washing',
  32: 'Dressing',
  33: 'Moving around indoors',
  34: 'Falls and stumbles',
  35: 'Eating and drinking',
  36: 'Medicines and treatment',
  37: 'Communicating',
  38: 'Days a week you need help',
  39: 'Hobbies, interests, social and religious activities',
  40: 'Someone to keep an eye on you',
  41: 'Days a week someone keeps an eye on you',
  42: 'Help at night',
  43: 'Nights a week you need help',
  44: 'Someone to watch over you at night',
  45: 'Nights a week someone watches over you',
  46: 'Anything else about your care needs',
  47: 'In hospital, a care home or similar place now',
  48: 'Out of hospital, a care home or similar place in the last 6 weeks',
  49: 'Constant Attendance Allowance',
  50: 'Account holder',
  51: 'Bank or building society',
  52: 'Sort code',
  53: 'Account number',
  54: 'Roll or reference number',
  55: 'Supporting statement: how often they see you',
  56: 'Supporting statement',
  57: 'Supporting statement: their relationship to you',
  58: 'Supporting statement: their name',
  59: 'Supporting statement: their address',
  60: 'Supporting statement: their phone number',
  63: 'Extra information',
  64: 'Declaration',
  65: 'Documents sent with this claim',
};

/** "Q31 Washing": the printed question number, never the registry's sub-part. */
const heading = (e: Entry): string => {
  const n = Number(e.q?.match(/^Q(\d+)/)?.[1]);
  return `Q${n} ${TITLE[n] ?? ''}`.trim();
};

// ---- Values ----------------------------------------------------------------

type Out = { values: Record<string, WidgetValue>; continuation: string[] };

const asText = (e: Entry, value: Value): string => {
  if (typeof value !== 'string')
    throw new TypeError(`AA1 ${e.q} ${e.id} expects text, got ${JSON.stringify(value)}`);
  return printable(value);
};

const pad = (n: string) => n.padStart(2, '0');

/** DD/MM/YYYY from either form; a rough date ("March 2025") stays as written. */
const ukDate = (value: string): string => {
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return `${pad(iso[3])}/${pad(iso[2])}/${iso[1]}`;
  const uk = value.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (uk) return `${pad(uk[1])}/${pad(uk[2])}/${uk[3]}`;
  return value;
};

/** The answer as it is written on the form. */
const formatted = (type: AnswerType, value: string): string => {
  switch (type) {
    case 'date':
    case 'approx_date':
      return ukDate(value);
    case 'ni_number':
      return value.replace(/[\s-]/g, '').toUpperCase();
    case 'sort_code':
    case 'account_number':
      return value.replace(/[\s-]/g, '');
    default:
      return value;
  }
};

/** The text for each of the entry's widgets, in order. */
const partsFor = (type: AnswerType, widgets: string[], value: string): string[] => {
  if (type === 'sort_code')
    return /^\d{6}$/.test(value) && widgets.length === 3
      ? [value.slice(0, 2), value.slice(2, 4), value.slice(4, 6)]
      : [];
  if (type === 'address') {
    // One line per box; lines beyond the last box join it.
    const lines = value.split('\n').map((l) => l.trim()).filter(Boolean);
    const n = widgets.length;
    return lines.length <= n
      ? lines
      : [...lines.slice(0, n - 1), lines.slice(n - 1).join(', ')];
  }
  return [value];
};

/** The answer in one line of Q63. */
const flat = (type: AnswerType, value: string) =>
  type === 'address'
    ? value.split('\n').map((l) => l.trim()).filter(Boolean).join(', ')
    : value;

/**
 * Write a text answer across its widgets. A part that does not fit is
 * shortened (a comb box is left empty: half an NI number is wrong), and the
 * whole answer is carried into Q63 under `label`.
 */
const writeText = (
  out: Out,
  type: AnswerType,
  widgets: string[],
  raw: string,
  label: string,
) => {
  const value = formatted(type, raw);
  if (!value) return;
  const parts = partsFor(type, widgets, value);
  let carried = parts.length === 0;
  parts.forEach((part, i) => {
    const widget = widgets[i];
    const box = BOXES[widget];
    if (!box) throw new Error(`AA1 widget ${widget} is not a text box in BOXES`);
    const text = box[2] === 's' ? singleLine(part) : part;
    if (fits(text, box)) {
      out.values[widget] = text;
      return;
    }
    carried = true;
    const short = typeof box[2] === 'number' ? '' : shortened(text, box);
    if (short) out.values[widget] = short;
  });
  if (carried) out.continuation.push(`${label}: ${flat(type, value)}`);
};

/**
 * The option an answer chose: answers are option labels, so every yes_no is
 * "Yes" or "No". A tick box's No exports "Off", which leaves it blank.
 */
const optionFor = (e: Entry, label: string): EntryOption => {
  const found = e.options?.find((o) => o.label === label);
  if (!found)
    throw new Error(`AA1 ${e.q} ${e.id}: "${label}" is not one of its options`);
  return found;
};

/** A widget takes the option's export value; "Off" options leave it blank. */
const tick = (out: Out, option: EntryOption) => {
  if (option.widget && option.exportValue !== 'Off')
    out.values[option.widget] = option.exportValue;
};

const writeChoices = (out: Out, e: Entry, value: Value) => {
  if (e.answerType !== 'multi_choice') {
    if (typeof value !== 'string')
      throw new TypeError(`AA1 ${e.q} ${e.id} expects an option label`);
    tick(out, optionFor(e, value.trim()));
    return;
  }
  if (!Array.isArray(value) || value.some((v) => typeof v !== 'string'))
    throw new TypeError(`AA1 ${e.q} ${e.id} expects a list of option labels`);
  const chosen = new Set((value as string[]).map((label) => optionFor(e, label.trim())));
  // Registry order decides which option a shared box shows. Only x34 shares
  // one: Fall and Stumble are kids of one field, so both cannot be ticked.
  const byWidget = new Map<string, EntryOption[]>();
  for (const option of e.options ?? [])
    if (chosen.has(option) && option.widget && option.exportValue !== 'Off')
      byWidget.set(option.widget, [...(byWidget.get(option.widget) ?? []), option]);
  for (const options of byWidget.values()) {
    tick(out, options[0]);
    if (options.length > 1)
      out.continuation.push(
        `${heading(e)}: ${options.map((o) => o.label).join(' and ')} both apply. The form takes one tick here, so ${options[0].label} is ticked.`,
      );
  }
};

// ---- Tables ----------------------------------------------------------------

type Column = {
  id: string;
  header: string;
  entry?: Entry;
  type: AnswerType;
  /**
   * The keys a row may hold this column's value under: its column id, and for
   * Q39 (whose registry names every cell) the cell ids too, so a row keyed
   * either way reads the same.
   */
  keys: string[];
  widgets: (row: number) => string[];
};

const columnsOf = (t: Entry): Column[] => {
  const table = t.table;
  if (!table) return [];
  const capacity = rowsOf(t.id);
  return table.columnIds.map((id, c) => {
    const entry = ENTRY[id];
    const header = table.columns[c] ?? id;
    const cells = table.cells;
    if (cells)
      return {
        id,
        header,
        entry,
        type: entry?.answerType ?? 'long_text',
        keys: [...new Set([id, ...cells.map((row) => row[c].id)])],
        widgets: (r) => (cells[r]?.[c] ? [cells[r][c].widget] : []),
      };
    if (!entry) throw new Error(`AA1 table ${t.id}: no registry entry for column ${id}`);
    const perRow = entry.widgets.length / capacity;
    if (!Number.isInteger(perRow))
      throw new Error(`AA1 table ${t.id}: ${id} has ${entry.widgets.length} widgets for ${capacity} rows`);
    return {
      id,
      header,
      entry,
      type: entry.answerType,
      keys: [id],
      widgets: (r) => entry.widgets.slice(r * perRow, (r + 1) * perRow),
    };
  });
};

const and = (v: Tri[]): Tri =>
  v.includes(false) ? false : v.includes(undefined) ? undefined : true;
const or = (v: Tri[]): Tri =>
  v.includes(true) ? true : v.includes(undefined) ? undefined : false;

/** A column's condition in one row: "filled, per row" reads that row's cell. */
const inRow = (c: Condition, row: Row, facts: Facts): Tri => {
  if ('all' in c) return and(c.all.map((x) => inRow(x, row, facts)));
  if ('any' in c) return or(c.any.map((x) => inRow(x, row, facts)));
  if ('op' in c && c.op === 'filled' && c.perRow) return !isEmpty(row[c.id]);
  return evaluate(c, facts);
};

const cellValue = (row: Row, column: Column): string => {
  const key = column.keys.find((k) => !isEmpty(row[k]));
  return key ? printable(row[key]) : '';
};

const writeTable = (out: Out, t: Entry, value: Value, facts: Facts) => {
  if (!Array.isArray(value) || value.some((v) => typeof v !== 'object'))
    throw new TypeError(`AA1 ${t.q} ${t.id} expects rows`);
  const columns = columnsOf(t);
  const capacity = rowsOf(t.id);
  const live = (row: Row, column: Column) =>
    !column.entry?.when || inRow(column.entry.when, row, facts) !== false;
  // Empty rows are dropped so the form's rows stay contiguous.
  const rows = (value as Row[]).filter((row) =>
    columns.some((column) => cellValue(row, column) !== ''),
  );
  rows.forEach((row, r) => {
    const label = `${heading(t)}, number ${r + 1}`;
    const cells = columns.filter((column) => live(row, column) && cellValue(row, column));
    if (r >= capacity) {
      // The form has no row for it: the whole row goes into Q63.
      out.continuation.push(
        `${label}: ${cells
          .map((column) => `${column.header}: ${flat(column.type, cellValue(row, column))}`)
          .join('; ')}`,
      );
      return;
    }
    for (const column of cells) {
      const raw = cellValue(row, column);
      const widgets = column.widgets(r);
      if (column.entry?.options?.length) {
        // A tick column (Q27 prescribed): the chosen option's state goes in
        // this row's own box.
        const option = optionFor(column.entry, raw);
        tick(out, { ...option, widget: widgets[0] });
        continue;
      }
      writeText(out, column.type, widgets, raw, `${label}, ${column.header}`);
    }
  });
};

// ---- The form ---------------------------------------------------------------

const EXTRA_INFORMATION = 'extraInformation';

/** The answer the PDF may show: confirmed, and for an entry that applies. */
const confirmed = (facts: Facts, e: Entry): Value | undefined =>
  statusOf(e, facts) === 'confirmed' && !isEmpty(facts[e.id].value)
    ? facts[e.id].value
    : undefined;

const digits = (value: Value | undefined) =>
  typeof value === 'string' ? value.replace(/\D/g, '') : '';

/** Split Q63 where it stops fitting; what is left goes on separate sheets. */
const fitQ63 = (text: string): { head: string; rest: string } => {
  const box = BOXES[Q63_WIDGET];
  if (fits(text, box)) return { head: text, rest: '' };
  const head = startThatFits(text, box, Q63_CONTINUED);
  const kept = head.slice(0, -Q63_CONTINUED.length).trimEnd();
  return { head, rest: text.slice(kept.length).trim() };
};

/** Every AA1 widget value the confirmed facts give, and what Q63 carries. */
export const widgetValues = (facts: Facts): AaWidgetValues => {
  const out: Out = { values: {}, continuation: [] };
  for (const e of ENTRIES) {
    if (e.id === EXTRA_INFORMATION || isFormOnly(e) || e.tableId) continue;
    const value = confirmed(facts, e);
    if (value === undefined) continue;
    if (e.answerType === 'table') writeTable(out, e, value, facts);
    else if (e.options?.some((o) => o.widget)) writeChoices(out, e, value);
    else if (e.widgets.length) {
      // Q7.2 is "daytime phone number, if different": a number already in
      // the mobile box is not repeated.
      const mobile = digits(confirmed(facts, ENTRY.mobile));
      if (e.id === 'phone' && mobile && digits(value) === mobile) continue;
      writeText(out, e.answerType, e.widgets, asText(e, value), heading(e));
    }
  }

  const own = confirmed(facts, ENTRY[EXTRA_INFORMATION]);
  const q63 = [own === undefined ? '' : asText(ENTRY[EXTRA_INFORMATION], own), ...out.continuation]
    .filter(Boolean)
    .join('\n\n');
  const { head, rest } = fitQ63(q63);
  if (head) out.values[Q63_WIDGET] = head;
  return {
    values: out.values,
    continuation: out.continuation,
    extraSheets: rest ? rest.split(/\n{2,}/) : [],
  };
};
